import 'twin.macro';
import * as React from 'react';
import tw from 'twin.macro';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { BaseModal } from '@restworld/ui-ds';
import { consumptionDetailModalOpenAtom } from './atoms';
import { useConsumptionDetails } from './use-consumption-details';
import PeriodFilterTabs, { type PeriodType } from './period-filter-tabs';
import ConsumptionTimeline from './consumption-timeline';
import PeriodCostSummary from './period-cost-summary';
import AllTimeSummary from './all-time-summary';
import { ContentContainer, Card } from './styled';
import { JOB_COLORS, COST_PER_DAY } from './constants';
import { getDays } from './utils';
import type { JobStats } from './types';

const ConsumptionDetailModal: React.FC = () => {
  const { t } = useTranslation('payment-and-invoices');
  const [isOpen, setIsOpen] = useAtom(consumptionDetailModalOpenAtom);
  const { data, isLoading } = useConsumptionDetails(isOpen);

  const today = React.useMemo(() => new Date(), []);

  const [period, setPeriod] = React.useState<PeriodType>('last_7');

  const logs = React.useMemo(() => data?.logs ?? [], [data]);

  const { start, end } = React.useMemo(() => {
    const e = new Date(today);
    const s = new Date(today);
    if (period === 'last_7') {
      s.setDate(today.getDate() - 6);
    } else if (period === 'last_30') {
      s.setDate(today.getDate() - 29);
    } else {
      s.setDate(today.getDate() - 89);
    }
    return { start: s, end: e };
  }, [period, today]);

  const days = React.useMemo(() => getDays(start, end), [start, end]);

  const logSet = React.useMemo(() => {
    const s = new Set<string>();
    logs.forEach((l) => s.add(`${l.jp_id}|${l.date}`));
    return s;
  }, [logs]);

  const jobColorMap = React.useMemo(() => {
    const uniqueIds = [...new Set(logs.map((l) => l.jp_id))];
    const map: Record<string, string> = {};
    uniqueIds.forEach((id, i) => {
      map[id] = JOB_COLORS[i % JOB_COLORS.length];
    });
    return map;
  }, [logs]);

  const jobTitleMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    logs.forEach((l) => {
      map[l.jp_id] = l.title;
    });
    return map;
  }, [logs]);

  const visibleJobIds = React.useMemo(() => {
    const ids = new Set<string>();
    days.forEach((d) => {
      if (d.date > today) return;
      Object.keys(jobColorMap).forEach((id) => {
        if (logSet.has(`${id}|${d.str}`)) ids.add(id);
      });
    });
    return [...ids];
  }, [days, logSet, jobColorMap, today]);

  const periodCost = React.useMemo(() => {
    let count = 0;
    days.forEach((d) => {
      if (d.date > today) return;
      visibleJobIds.forEach((id) => {
        if (logSet.has(`${id}|${d.str}`)) count++;
      });
    });
    return count * COST_PER_DAY;
  }, [days, visibleJobIds, logSet, today]);

  const allJobStats = React.useMemo<JobStats[]>(() => {
    const stats: Record<string, JobStats> = {};
    logs.forEach((l) => {
      if (!stats[l.jp_id]) {
        stats[l.jp_id] = { jp_id: l.jp_id, title: l.title, days: 0, cost: 0, firstDate: l.date };
      }
      stats[l.jp_id].days++;
      stats[l.jp_id].cost += COST_PER_DAY;
      if (l.date < stats[l.jp_id].firstDate) stats[l.jp_id].firstDate = l.date;
    });
    return Object.values(stats).sort((a, b) => (b.firstDate > a.firstDate ? 1 : b.firstDate < a.firstDate ? -1 : 0));
  }, [logs]);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  return (
    <BaseModal
      open={isOpen}
      onClose={handleClose}
      title={t('consumption_detail_title')}
      showCloseIcon
      noPadding
      modalStyles={[tw`md:max-w-[900px] md:w-full`]}
      modalBodyStyles={[tw`max-h-[75vh]`]}
      headerStyles={[tw`px-4 pt-4 pb-6`]}
    >
      <ContentContainer>
        {isLoading ? (
          <div tw="flex items-center justify-center py-12">
            <div tw="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : (
          <>
            {/* Card 1: Period timeline + cost */}
            <Card>
              <PeriodFilterTabs period={period} onPeriodChange={setPeriod} />
              <ConsumptionTimeline
                days={days}
                visibleJobIds={visibleJobIds}
                jobColorMap={jobColorMap}
                jobTitleMap={jobTitleMap}
                logSet={logSet}
                today={today}
              />
              <PeriodCostSummary cost={periodCost} />
            </Card>

            {/* Card 2: All-time summary per position */}
            <Card>
              <AllTimeSummary allJobStats={allJobStats} jobColorMap={jobColorMap} />
            </Card>
          </>
        )}
      </ContentContainer>
    </BaseModal>
  );
};

export default ConsumptionDetailModal;
